import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismAccordion,
  PrismBadge,
  PrismCheckbox,
  PrismIcon,
  PrismNumberInput,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import {
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  NUMBER_INPUT_MAX_VALUE,
  OVERRIDE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './stay-restrictions-panel.module.css';

interface StayRestrictionsPanelSignature {
  Args: {
    automationOn: boolean;
    errorMessage: string | null;
    formattedMinLos: string;
    hasMinLosOverride: boolean;
    isTouched: boolean;
    minLos: number | null;
    minLosRestrictionType: MinLosRestrictionType;
    onMinLosBlur: () => void;
    onMinLosTypeChange: (event: Event) => void;
    onToggleAutomation: (event: Event) => void;
    onUpdateMinLos: (value: number | null) => void;
  };
}

const StayRestrictionsPanel = <template>
  <PrismAccordion
    @initiallyOpened={{true}}
    @title={{t "general.hotel"}}
    class="mx-500 my-400"
    data-test-id="restrictions-panel"
  >
    <:summaryAfter>
      {{#if @hasMinLosOverride}}
        <PrismBadge
          @iconName={{getOverrideVariant OVERRIDE_TYPES.MIN_LOS}}
          @label={{t "pricing_manager.automation_off"}}
          @type="warning"
        />
      {{/if}}
    </:summaryAfter>
    <:default>
      <div
        class="{{styles.automation-container}}
          mb-400 flex items-center justify-between p-400"
      >
        <PrismCheckbox
          @checked={{@automationOn}}
          @onChange={{@onToggleAutomation}}
          data-test-id="restrictions-automation-checkbox"
        >
          <p class={{if @automationOn styles.automation-label-on}}>
            {{t "pricing_manager.automation_on"}}
          </p>
        </PrismCheckbox>
        <div
          class="flex items-center gap-100
            {{unless @automationOn styles.automation-value-off}}"
        >
          <PrismIcon
            @color={{if @automationOn "on-intelligence" "disabled"}}
            @name={{if @automationOn "automation" "automation-off"}}
            @size="small"
          />
          <p
            class="text-300--regular"
            data-test-id="restrictions-panel-automation-value"
          >
            {{@formattedMinLos}}
          </p>
        </div>
      </div>

      {{#unless @automationOn}}
        <div class={{styles.divider}} />
        <div class="mt-400 flex flex-col gap-200">
          <PrismRadioGroup
            @isHorizontal={{true}}
            @name="min-los-type"
            @onChange={{@onMinLosTypeChange}}
            @value={{@minLosRestrictionType}}
            class={{styles.radio-group}}
            as |RadioGroup|
          >
            <RadioGroup.input
              @value={{MIN_LOS_RESTRICTION_TYPE.MIN_LOS}}
              class="items-center"
            >
              <div class="flex items-center gap-200">
                {{t "pricing_manager.restrictions.min_los_label"}}
                <div class={{styles.min-los-input}}>
                  <PrismNumberInput
                    @disabled={{eq
                      @minLosRestrictionType
                      MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
                    }}
                    @hasErrors={{if @errorMessage true false}}
                    @inlineLabel={{t "pricing_manager.restrictions.nights"}}
                    @isTouched={{@isTouched}}
                    @max={{NUMBER_INPUT_MAX_VALUE}}
                    @min={{0}}
                    @onValueChange={{@onUpdateMinLos}}
                    @removePadding={{true}}
                    @required={{eq
                      @minLosRestrictionType
                      MIN_LOS_RESTRICTION_TYPE.MIN_LOS
                    }}
                    @value={{@minLos}}
                    data-test-id="min-los-value-input"
                    {{on "blur" @onMinLosBlur}}
                  />
                </div>
              </div>
            </RadioGroup.input>
            <RadioGroup.input
              @value={{MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION}}
            >
              {{t "pricing_manager.restrictions.no_min_los"}}
            </RadioGroup.input>
          </PrismRadioGroup>
          {{#if @errorMessage}}
            <div class={{styles.error-message}}>
              <PrismIcon @color="critical" @name="circle-minus" @size="small" />
              <p class="text-200--medium">{{@errorMessage}}</p>
            </div>
          {{/if}}
        </div>
      {{/unless}}
    </:default>
  </PrismAccordion>
</template> satisfies TOC<StayRestrictionsPanelSignature>;

export default StayRestrictionsPanel;
